/*
 This file is part of GNU Taler
 (C) 2022-2025 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  HttpRequestLibrary,
  HttpRequestOptions,
  HttpResponse,
  readSuccessResponseJsonOrThrow,
  readTalerErrorResponse,
} from "../http-common.js";
import { HttpStatusCode } from "../http-status-codes.js";
import { createPlatformHttpLib } from "../http.js";
import { LibtoolVersion } from "../libtool-version.js";
import {
  OperationAlternative,
  OperationFail,
  OperationOk,
  ResultByMethod,
  carefullyParseConfig,
  opEmptySuccess,
  opFixedAlternativeFailure,
  opFixedSuccess,
  opKnownAlternativeHttpFailure,
  opKnownFailure,
  opKnownHttpFailure,
  opSuccessFromHttp,
  opUnknownHttpFailure,
} from "../operation.js";
import { encodeCrock } from "../taler-crypto.js";
import {
  AccessToken,
  CoinHistoryResponse,
  EddsaPublicKeyString,
  EddsaSignatureString,
  LongPollParams,
  OfficerSession,
  PaginationParams,
  codecForCoinHistoryResponse,
} from "../types-taler-common.js";
import {
  AccountKycStatus,
  AmlDecisionRequest,
  AmlDecisionsResponse,
  AmlOfficerInfo,
  AvailableMeasureSummary,
  BatchDepositSuccess,
  BlindingPrepareRequest,
  BlindingPrepareResponse,
  EventCounter,
  ExchangeBatchDepositRequest,
  ExchangeGetContractResponse,
  ExchangeKeysResponse,
  ExchangeKycUploadFormRequest,
  ExchangeMeltRequestV2,
  ExchangeMeltResponse,
  ExchangeMergeConflictResponse,
  ExchangeMergeSuccessResponse,
  ExchangePurseDeposits,
  ExchangePurseMergeRequest,
  ExchangePurseStatus,
  ExchangeRefreshRevealRequestV2,
  ExchangeRefundRequest,
  ExchangeRefundSuccessResponse,
  ExchangeReservePurseRequest,
  ExchangeRevealMeltResponseV2,
  ExchangeTransferList,
  ExchangeWithdrawRequest,
  ExchangeWithdrawResponse,
  KycAttributes,
  KycProcessClientInformation,
  KycProcessClientInformationWithEtag,
  KycProcessStartInformation,
  KycRequirementInformationId,
  LegitimizationMeasuresList,
  LegitimizationNeededResponse,
  PurseConflict,
  PurseConflictPartial,
  PurseCreateSuccessResponse,
  PurseDepositSuccessResponse,
  RecoupConfirmation,
  RecoupRefreshRequest,
  RecoupRequest,
  TrackTransactionAccepted,
  TrackTransactionWired,
  WalletKycCheckResponse,
  WalletKycRequest,
  codecForAccountKycStatus,
  codecForBatchDepositSuccess,
  codecForBlindingPrepareResponse,
  codecForAmlDecisionsAccounts,
  codecForAmlDecisionsResponse,
  codecForAmlKycAttributes,
  codecForAmlOfficerInfo,
  codecForAmlStatisticsResponse,
  codecForAmlWalletKycCheckResponse,
  codecForAvailableMeasureSummary,
  codecForExchangeConfig,
  codecForExchangeGetContractResponse,
  codecForExchangeKeysResponse,
  codecForExchangeMeltResponse,
  codecForExchangeMergeConflictResponse,
  codecForExchangeMergeSuccessResponse,
  codecForExchangePurseStatus,
  codecForExchangeRevealMeltResponseV2,
  codecForExchangeRefundSuccessResponse,
  codecForExchangeTransferList,
  codecForExchangeWithdrawResponse,
  codecForKycProcessClientInformation,
  codecForKycProcessStartInformation,
  codecForLegitimizationMeasuresList,
  codecForLegitimizationNeededResponse,
  codecForPurseConflict,
  codecForPurseConflictPartial,
  codecForPurseCreateSuccessResponse,
  codecForPurseDepositSuccessResponse,
  codecForRecoupConfirmation,
  codecForTackTransactionAccepted,
  codecForTackTransactionWired,
} from "../types-taler-exchange.js";
import {
  ReserveCloseRequest,
  ReserveCloseResponse,
  ReserveHistory,
  ReserveStatus,
  codecForReserveCloseResponse,
  codecForReserveHistory,
  codecForReserveStatus,
} from "../ReserveStatus.js";
import {
  CacheEvictor,
  addLongPollingParam,
  addPaginationParams,
  nullEvictor,
  pathSegment,
} from "./utils.js";

import { CancellationToken } from "../CancellationToken.js";
import { AmountJson, Amounts } from "../amounts.js";
import { Logger } from "../logging.js";
import { LongpollQueue } from "../longpoll-queue.js";
import { opKnownFailureWithBody, opKnownTalerFailure } from "../operation.js";
import { PaytoHash } from "../payto.js";
import { TalerErrorCode } from "../taler-error-codes.js";
import { signAmlDecision, signAmlQuery } from "../taler-signatures.js";
import { Duration } from "../time.js";
import { AbsoluteTime } from "../time.js";
import { EmptyObject } from "../types-taler-wallet.js";

export type TalerExchangeResultByMethod2<
  prop extends keyof TalerExchangeHttpClient,
> = ResultByMethod<TalerExchangeHttpClient, prop>;

const logger = new Logger("exchange-client.ts");

export enum TalerExchangeCacheEviction {
  UPLOAD_KYC_FORM,
  MAKE_AML_DECISION,
}

/**
 * Render `formats`, most preferred first, as an "Accept" header value.
 *
 * Position in the list carries no meaning in HTTP: entries without a weight
 * all default to q=1, which makes them equally acceptable and leaves the
 * choice to the server. Preference has to be expressed as descending weights
 * (RFC 9110, section 12.5.1).
 */
export function formatAcceptHeader(formats: string[]): string {
  return formats
    .map((format, idx) => {
      if (0 === idx) {
        // Most preferred, q defaults to 1.
        return format;
      }
      // Weights must stay above 0, as q=0 means "not acceptable", and
      // carry at most three digits after the decimal point.
      const weight = Math.max(0.001, (formats.length - idx) / formats.length);
      return `${format};q=${weight.toFixed(3).replace(/0+$/, "")}`;
    })
    .join(", ");
}

function addYesNoFilter(
  url: URL,
  name: string,
  value: boolean | undefined,
): void {
  if (value !== undefined) {
    url.searchParams.set(name, value ? "YES" : "NO");
  }
}

/**
 * Client library for the GNU Taler exchange service.
 */
export class TalerExchangeHttpClient {
  // Protocol v25 requires a legacy withdrawal commitment that this wallet did
  // not persist.  Do not advertise compatibility with a version whose recoup
  // path cannot be implemented safely.
  public static readonly SUPPORTED_EXCHANGE_PROTOCOL_VERSION = "34:0:8";
  private httpLib: HttpRequestLibrary;
  private cacheEvictor: CacheEvictor<TalerExchangeCacheEviction>;
  private preventCompression: boolean;
  private cancelationToken: CancellationToken;
  private longPollQueue: LongpollQueue;
  /**
   * Timeout applied to every request made by this client. The client is
   * short-lived and constructed per logical operation, so the timeout is set
   * once here rather than per call.
   */
  private defaultTimeout: Duration | undefined;

  constructor(
    readonly baseUrl: string,
    params: {
      httpClient?: HttpRequestLibrary;
      cacheEvictor?: CacheEvictor<TalerExchangeCacheEviction>;
      preventCompression?: boolean;
      cancelationToken?: CancellationToken;
      longPollQueue?: LongpollQueue;
      timeout?: Duration;
    } = {},
  ) {
    this.httpLib = params.httpClient ?? createPlatformHttpLib();
    this.cacheEvictor = params.cacheEvictor ?? nullEvictor;
    this.preventCompression = !!params.preventCompression;
    this.cancelationToken =
      params.cancelationToken ?? CancellationToken.CONTINUE;
    this.longPollQueue = params.longPollQueue ?? new LongpollQueue();
    this.defaultTimeout = params.timeout;
  }

  static isCompatible(version: string): boolean {
    const compare = LibtoolVersion.compare(
      TalerExchangeHttpClient.SUPPORTED_EXCHANGE_PROTOCOL_VERSION,
      version,
    );
    return compare?.compatible ?? false;
  }

  private async fetch(
    url_or_path: URL | string,
    opts: HttpRequestOptions = {},
    longpoll: boolean = false,
  ): Promise<HttpResponse> {
    const url =
      typeof url_or_path == "string"
        ? new URL(url_or_path, this.baseUrl)
        : url_or_path;
    if (longpoll || url.searchParams.has("timeout_ms")) {
      return this.longPollQueue.run(
        url,
        this.cancelationToken,
        async (timeoutMs) => {
          url.searchParams.set("timeout_ms", String(timeoutMs));
          return this.httpLib.fetch(url.href, {
            timeout: this.defaultTimeout,
            cancellationToken: this.cancelationToken,
            ...opts,
          });
        },
      );
    } else {
      return this.httpLib.fetch(url.href, {
        timeout: this.defaultTimeout,
        cancellationToken: this.cancelationToken,
        ...opts,
      });
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--seed
   *
   */
  async getSeed() {
    const resp = await this.fetch("seed");
    switch (resp.status) {
      case HttpStatusCode.Ok: {
        const buffer = await resp.bytes();
        const uintar = new Uint8Array(buffer);
        return opFixedSuccess(resp, uintar);
      }
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }
  /**
   * https://docs.taler.net/core/api-exchange.html#get--config
   *
   */
  async getConfig() {
    const resp = await this.fetch("config");
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return carefullyParseConfig(
          "taler-exchange",
          TalerExchangeHttpClient.SUPPORTED_EXCHANGE_PROTOCOL_VERSION,
          resp,
          codecForExchangeConfig(),
        );
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-merchant.html#get--config
   *
   * PARTIALLY IMPLEMENTED!!
   */
  async getKeys(
    opts: { noCache?: boolean; lastIssueDate?: number } = {},
  ): Promise<
    OperationOk<ExchangeKeysResponse> | OperationFail<HttpStatusCode.NotFound>
  > {
    const headers: Record<string, string> = {};
    if (opts.noCache) {
      headers["cache-control"] = "no-cache";
    }
    const url = new URL("keys", this.baseUrl);
    if (opts.lastIssueDate != null) {
      // Cherry-picking: the exchange leaves out every denomination that
      // started before this timestamp.  It only does so when the value is
      // exactly the stamp_start of a denomination it still offers, and
      // answers with the full list otherwise, so a stale value costs
      // bandwidth but never correctness.
      url.searchParams.set("last_issue_date", String(opts.lastIssueDate));
    }
    const resp = await this.fetch(url, { headers });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeKeysResponse());
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  async getTermsText(
    args: {
      acceptLanguage?: string;
      acceptFormats?: string[];
    } = {},
  ): Promise<OperationOk<{ text: string; etag: string | undefined }>> {
    const acceptFormats = args.acceptFormats ?? ["text/plain"];
    const headers = {
      Accept: formatAcceptHeader(acceptFormats),
      "Accept-Language": args.acceptLanguage,
    };
    const resp = await this.fetch("terms", {
      headers,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opFixedSuccess(resp, {
          text: await resp.text(),
          // The HTTP ETag is a hash of the served document and differs per
          // language and format; acceptance is keyed on the legal version.
          etag: resp.headers.get("taler-terms-version") || undefined,
        });
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  // WALLET TO WALLET

  /**
   * https://docs.taler.net/core/api-exchange.html#get--purses-$PURSE_PUB-merge
   *
   */
  async getPurseStatusAtMerge(
    pursePub: string,
    longpoll: boolean = false,
  ): Promise<
    | OperationOk<ExchangePurseStatus>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.Gone>
  > {
    const resp = await this.fetch(`purses/${pursePub}/merge`, {}, longpoll);
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangePurseStatus());
      case HttpStatusCode.Gone:
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--purses-$PURSE_PUB-deposit
   *
   */
  async getPurseStatusAtDeposit(
    pursePub: string,
    longpoll: boolean = false,
  ): Promise<
    | OperationOk<ExchangePurseStatus>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.Gone>
  > {
    const resp = await this.fetch(`purses/${pursePub}/deposit`, {}, longpoll);
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangePurseStatus());
      case HttpStatusCode.Gone:
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--purses-$PURSE_PUB-create
   *
   */
  async createPurseFromDeposit(
    pursePub: string,
    body: any, // FIXME
  ): Promise<
    | OperationOk<PurseCreateSuccessResponse>
    | OperationFail<HttpStatusCode.Forbidden>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationAlternative<HttpStatusCode.Conflict, PurseConflict>
    | OperationFail<HttpStatusCode.TooEarly>
    | OperationFail<TalerErrorCode.EXCHANGE_PURSE_CREATE_EXPIRATION_BEFORE_NOW>
    | OperationFail<TalerErrorCode.EXCHANGE_PURSE_CREATE_EXPIRATION_IS_NEVER>
  > {
    const resp = await this.fetch(`purses/${pursePub}/create`, {
      method: "POST",
      body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForPurseCreateSuccessResponse());
      case HttpStatusCode.Conflict:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForPurseConflict(),
        );
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.TooEarly:
        return opKnownHttpFailure(resp.status, resp);
      case HttpStatusCode.BadRequest: {
        const details = await readTalerErrorResponse(resp);
        switch (details.code) {
          case TalerErrorCode.EXCHANGE_PURSE_CREATE_EXPIRATION_BEFORE_NOW:
          case TalerErrorCode.EXCHANGE_PURSE_CREATE_EXPIRATION_IS_NEVER:
            return opKnownTalerFailure(resp, details.code, details);
          default:
            return opUnknownHttpFailure(resp, details);
        }
      }
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#delete--purses-$PURSE_PUB
   *
   */
  async deletePurse(
    pursePub: string,
    purseSig: string,
  ): Promise<
    | OperationOk<undefined>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.Conflict>
    | OperationFail<HttpStatusCode.Forbidden>
  > {
    const resp = await this.fetch(`purses/${pursePub}`, {
      method: "DELETE",
      headers: {
        "taler-purse-signature": purseSig,
      },
    });
    switch (resp.status) {
      case HttpStatusCode.NoContent:
        return opEmptySuccess(resp);
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      case HttpStatusCode.Forbidden:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * POST /purses/$PURSE_PUB/merge
   *
   * https://docs.taler.net/core/api-exchange.html#post--purses-$PURSE_PUB-merge
   */
  async postPurseMerge(
    pursePub: string,
    body: ExchangePurseMergeRequest,
  ): Promise<
    | OperationOk<ExchangeMergeSuccessResponse>
    | OperationAlternative<
        HttpStatusCode.UnavailableForLegalReasons,
        LegitimizationNeededResponse
      >
    | OperationAlternative<
        HttpStatusCode.Conflict,
        ExchangeMergeConflictResponse
      >
    | OperationFail<HttpStatusCode.Forbidden>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.Gone>
  > {
    const resp = await this.fetch(`purses/${pursePub}/merge`, {
      method: "POST",
      body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeMergeSuccessResponse());
      case HttpStatusCode.UnavailableForLegalReasons:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForLegitimizationNeededResponse(),
        );
      case HttpStatusCode.Conflict:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForExchangeMergeConflictResponse(),
        );
      case HttpStatusCode.Gone:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--reserves-$RESERVE_PUB-purse
   *
   */
  async createPurseFromReserve(
    pursePub: string,
    body: ExchangeReservePurseRequest,
  ): Promise<
    | OperationOk<PurseCreateSuccessResponse>
    | OperationFail<HttpStatusCode.PaymentRequired>
    | OperationFail<HttpStatusCode.Forbidden>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<TalerErrorCode.EXCHANGE_RESERVES_PURSE_EXPIRATION_BEFORE_NOW>
    | OperationAlternative<HttpStatusCode.Conflict, PurseConflictPartial>
    | OperationAlternative<
        HttpStatusCode.UnavailableForLegalReasons,
        LegitimizationNeededResponse
      >
  > {
    const resp = await this.fetch(`reserves/${pursePub}/purse`, {
      method: "POST",
      body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForPurseCreateSuccessResponse());
      case HttpStatusCode.PaymentRequired:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      case HttpStatusCode.Conflict:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForPurseConflictPartial(),
        );
      case HttpStatusCode.UnavailableForLegalReasons:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForLegitimizationNeededResponse(),
        );
      case HttpStatusCode.BadRequest: {
        const details = await readTalerErrorResponse(resp);
        switch (details.code) {
          case TalerErrorCode.EXCHANGE_RESERVES_PURSE_EXPIRATION_BEFORE_NOW:
            return opKnownTalerFailure(resp, details.code, details);
          default:
            return opUnknownHttpFailure(resp, details);
        }
      }
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--contracts-$CONTRACT_PUB
   *
   */
  async getContract(
    pursePub: string,
  ): Promise<
    | OperationOk<ExchangeGetContractResponse>
    | OperationFail<HttpStatusCode.NotFound>
  > {
    const resp = await this.fetch(`contracts/${pursePub}`);
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeGetContractResponse());
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--purses-$PURSE_PUB-deposit
   *
   */
  async depositIntoPurse(
    pursePub: string,
    body: ExchangePurseDeposits,
  ): Promise<
    | OperationOk<PurseDepositSuccessResponse>
    | OperationAlternative<HttpStatusCode.Conflict, PurseConflict>
    | OperationFail<HttpStatusCode.Forbidden>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.Gone>
  > {
    const resp = await this.fetch(`purses/${pursePub}/deposit`, {
      method: "POST",
      body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForPurseDepositSuccessResponse());
      case HttpStatusCode.Conflict:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForPurseConflict(),
        );
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Gone:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  // WADS

  /**
   * https://docs.taler.net/core/api-exchange.html#get--wads-$WAD_ID
   *
   */
  async getWadInfo(): Promise<never> {
    throw Error("not yet implemented");
  }

  //
  // KYC
  //

  /**
   * https://docs.taler.net/core/api-exchange.html#post--kyc-wallet
   *
   */
  async notifyKycBalanceLimit(
    body: WalletKycRequest,
  ): Promise<
    | OperationOk<WalletKycCheckResponse>
    | OperationOk<undefined>
    | OperationFail<HttpStatusCode.Forbidden>
    | OperationAlternative<
        HttpStatusCode.UnavailableForLegalReasons,
        LegitimizationNeededResponse
      >
  > {
    // Long-polls until the wallet's KYC/balance limit is resolved.
    const resp = await this.fetch(
      `kyc-wallet`,
      {
        method: "POST",
        body,
      },
      true,
    );

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForAmlWalletKycCheckResponse());
      case HttpStatusCode.NoContent:
        return opEmptySuccess(resp);
      case HttpStatusCode.Forbidden:
        return opKnownHttpFailure(resp.status, resp);
      case HttpStatusCode.UnavailableForLegalReasons:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForLegitimizationNeededResponse(),
        );
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--kyc-check-$H_NORMALIZED_PAYTO
   *
   */
  async checkKycStatus(args: {
    paytoHash: string;
    accountPub: EddsaPublicKeyString;
    accountSig: EddsaSignatureString;
    longpoll?: boolean;
    awaitAuth?: boolean;
    /**
     * Long-poll target: return once the account reaches this KYC state.
     */
    lpt?: number;
    /**
     * Long-poll until the account's rule generation is at least this value.
     */
    minRule?: number;
  }): Promise<
    | OperationOk<AccountKycStatus>
    | OperationAlternative<HttpStatusCode.Accepted, AccountKycStatus>
    | OperationAlternative<HttpStatusCode.NoContent, void>
    | OperationFail<
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
      >
  > {
    const { paytoHash, accountPub, accountSig, longpoll, awaitAuth } = args;
    const url = new URL(`kyc-check/${pathSegment(paytoHash)}`, this.baseUrl);
    if (awaitAuth !== undefined) {
      url.searchParams.set("await_auth", awaitAuth ? "YES" : "NO");
    }
    if (args.lpt !== undefined) {
      url.searchParams.set("lpt", String(args.lpt));
    }
    if (args.minRule !== undefined) {
      url.searchParams.set("min_rule", String(args.minRule));
    }

    const resp = await this.fetch(
      url,
      {
        headers: {
          "Account-Owner-Signature": accountSig,
          "Account-Owner-Pub": accountPub,
        },
      },
      longpoll,
    );

    switch (resp.status) {
      case HttpStatusCode.Ok: // means there are voluntary checks
        return opSuccessFromHttp(resp, codecForAccountKycStatus());
      case HttpStatusCode.Accepted: // means there are required checks
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForAccountKycStatus(),
        );
      case HttpStatusCode.NoContent: // no checks can be done
        return opKnownFailureWithBody(resp, resp.status, undefined);
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * Do a /kyc-check request, but don't specify
   * the account pub explicitly.
   *
   * Deprecated, but used in tests.
   */
  async testingCheckKycStatusNoPub(args: {
    paytoHash: string;
    accountSig: EddsaSignatureString;
    longpoll?: boolean;
    awaitAuth?: boolean;
  }): Promise<
    | OperationOk<AccountKycStatus>
    | OperationAlternative<HttpStatusCode.Accepted, AccountKycStatus>
    | OperationAlternative<HttpStatusCode.NoContent, void>
    | OperationFail<
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
      >
  > {
    const { paytoHash, accountSig, longpoll, awaitAuth } = args;
    const url = new URL(`kyc-check/${pathSegment(paytoHash)}`, this.baseUrl);
    if (awaitAuth !== undefined) {
      url.searchParams.set("await_auth", awaitAuth ? "YES" : "NO");
    }

    const resp = await this.fetch(
      url,
      {
        headers: {
          "Account-Owner-Signature": accountSig,
        },
      },
      longpoll,
    );

    switch (resp.status) {
      case HttpStatusCode.Ok: // means there are voluntary checks
        return opSuccessFromHttp(resp, codecForAccountKycStatus());
      case HttpStatusCode.Accepted: // means there are required checks
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForAccountKycStatus(),
        );
      case HttpStatusCode.NoContent: // no checks can be done
        return opKnownFailureWithBody(resp, resp.status, undefined);
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--kyc-info-$ACCESS_TOKEN
   *
   */
  async checkKycInfo(
    token: AccessToken,
    known: KycRequirementInformationId[] = [],
    longpoll: boolean = false,
  ): Promise<
    | OperationOk<KycProcessClientInformation>
    | OperationAlternative<HttpStatusCode.Accepted, EmptyObject>
    | OperationAlternative<HttpStatusCode.NoContent, EmptyObject>
    | OperationFail<HttpStatusCode.NotModified>
  > {
    const resp = await this.fetch(
      `kyc-info/${token}`,
      {
        method: "GET",
        headers: {
          "If-None-Match": known.length
            ? known.map((d) => `"${d}"`).join(",")
            : undefined,
        },
      },
      longpoll,
    );
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForKycProcessClientInformation());
      case HttpStatusCode.Accepted:
      case HttpStatusCode.NoContent:
        return opFixedAlternativeFailure(resp, resp.status, {});
      case HttpStatusCode.NotModified:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * SPA-Specific version of checkKycInfo
   *
   * FIXME: Unify with checkKycInfo
   */
  async checkKycInfoSpa(
    token: AccessToken,
    etag: string | undefined,
    params: LongPollParams = {},
  ) {
    const url = new URL(`kyc-info/${pathSegment(token)}`, this.baseUrl);

    addLongPollingParam(url, params);

    const resp = await this.httpLib.fetch(url.href, {
      method: "GET",
      headers: {
        "If-None-Match": !etag ? undefined : `"${etag}"`,
      },
    });
    switch (resp.status) {
      case HttpStatusCode.Ok: {
        // we need to add the etag to the response because the
        // client needs it to repeat the request and
        // do the long polling
        const etagRaw = resp.headers.get("etag") ?? undefined;
        let etag: string | undefined;
        if (
          etagRaw != null &&
          etagRaw.startsWith('"') &&
          etagRaw.endsWith('"')
        ) {
          etag = etagRaw.substring(1, etagRaw.length - 1);
        } else if (etagRaw == null) {
          // No ETag, fine.
          etag = etagRaw;
        } else {
          logger.warn(`malformed ETag header in kyc-info response`);
        }
        const body = await readSuccessResponseJsonOrThrow(
          resp,
          codecForKycProcessClientInformation(),
        );
        return opFixedSuccess<KycProcessClientInformationWithEtag>(resp, {
          ...body,
          etag,
        });
      }
      case HttpStatusCode.Accepted:
        return opKnownHttpFailure(resp.status, resp);
      case HttpStatusCode.NoContent:
        return opKnownFailure(resp, resp.status);
      case HttpStatusCode.NotModified:
        // do not read details from response
        return opKnownFailure(resp, resp.status);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--kyc-upload-$ID
   *
   */
  async uploadKycForm<T extends ExchangeKycUploadFormRequest>(
    requirement: KycRequirementInformationId,
    body: T,
  ): Promise<
    | OperationOk<undefined>
    | OperationFail<HttpStatusCode.Conflict>
    | OperationFail<HttpStatusCode.InternalServerError>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.PayloadTooLarge>
  > {
    const resp = await this.fetch(`kyc-upload/${requirement}`, {
      method: "POST",
      body,
      compress: this.preventCompression ? undefined : "deflate",
    });
    switch (resp.status) {
      case HttpStatusCode.NoContent: {
        this.cacheEvictor.notifySuccess(
          TalerExchangeCacheEviction.UPLOAD_KYC_FORM,
        );
        return opEmptySuccess(resp);
      }
      case HttpStatusCode.NotFound:
      case HttpStatusCode.InternalServerError:
      case HttpStatusCode.Conflict:
      case HttpStatusCode.PayloadTooLarge:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--kyc-start-$ID
   *
   */
  async startExternalKycProcess(
    requirement: KycRequirementInformationId,
    body: object = {},
  ): Promise<
    | OperationFail<
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
        | HttpStatusCode.PayloadTooLarge
      >
    | OperationOk<KycProcessStartInformation>
  > {
    const resp = await this.fetch(`kyc-start/${requirement}`, {
      method: "POST",
      body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForKycProcessStartInformation());
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
      case HttpStatusCode.PayloadTooLarge:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--kyc-proof-$PROVIDER_NAME?state=$H_PAYTO
   *
   */
  async completeExternalKycProcess(
    provider: string,
    state: string,
    code: string,
  ) {
    const url = new URL(
      `kyc-proof/${encodeURIComponent(provider)}`,
      this.baseUrl,
    );
    url.searchParams.set("state", state);
    url.searchParams.set("code", code);
    const resp = await this.fetch(url.href, {
      method: "GET",
      redirect: "manual",
    });

    switch (resp.status) {
      case HttpStatusCode.SeeOther:
        return opEmptySuccess(resp);
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  //
  // AML operations
  //

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB
   */
  async getAmlOfficer(
    auth: OfficerSession,
  ): Promise<
    | OperationOk<AmlOfficerInfo>
    | OperationFail<
        | HttpStatusCode.BadRequest
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
      >
  > {
    const resp = await this.fetch(`aml/${pathSegment(auth.id)}`, {
      method: "GET",
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForAmlOfficerInfo());
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-measures
   *
   */
  async getAmlMeasures(
    auth: OfficerSession,
  ): Promise<
    | OperationOk<AvailableMeasureSummary>
    | OperationFail<
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
      >
  > {
    const resp = await this.fetch(`aml/${auth.id}/measures`, {
      method: "GET",
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForAvailableMeasureSummary());
      case HttpStatusCode.Conflict:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Forbidden:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-kyc-statistics-$NAMES
   *
   */
  async getAmlKycStatistics(
    auth: OfficerSession,
    names: string[],
    filter: {
      since?: AbsoluteTime;
      until?: AbsoluteTime;
    } = {},
  ) {
    const url = new URL(
      `aml/${pathSegment(auth.id)}/kyc-statistics/${names.join(" ")}`,
      this.baseUrl,
    );

    if (filter.since !== undefined) {
      const { t_s } = AbsoluteTime.toProtocolTimestamp(filter.since);
      if (t_s !== "never") {
        url.searchParams.set("start_date", String(t_s));
      }
    }
    if (filter.until !== undefined) {
      const { t_s } = AbsoluteTime.toProtocolTimestamp(filter.until);
      if (t_s !== "never") {
        url.searchParams.set("end_date", String(t_s));
      }
    }

    const resp = await this.fetch(url, {
      method: "GET",
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForAmlStatisticsResponse());
      case HttpStatusCode.NoContent: {
        return opFixedSuccess(resp, {
          statistics: names.map(
            (name) => ({ counter: 0, event: name }) as EventCounter,
          ),
        });
      }
      case HttpStatusCode.Conflict:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Forbidden:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-accounts
   *
   */
  async getAmlAccounts(
    auth: OfficerSession,
    params: PaginationParams & {
      account?: PaytoHash;
      highRisk?: boolean;
      open?: boolean;
      investigation?: boolean;
    } = {},
  ) {
    const url = new URL(`aml/${pathSegment(auth.id)}/accounts`, this.baseUrl);

    addPaginationParams(url, params);
    if (params.account !== undefined) {
      url.searchParams.set("h_payto", params.account);
    }
    addYesNoFilter(url, "investigation", params.investigation);
    addYesNoFilter(url, "open", params.open);
    addYesNoFilter(url, "high_risk", params.highRisk);

    const resp = await this.fetch(url, {
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForAmlDecisionsAccounts());
      case HttpStatusCode.NoContent:
        return opFixedSuccess(resp, { accounts: [] });
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-accounts
   *
   */
  async getAmlAccountsAsOtherFormat(
    auth: OfficerSession,
    mime: "text/csv" | "application/vnd.ms-excel" | "application/json",
    params: {
      account?: PaytoHash;
      highRisk?: boolean;
      open?: boolean;
      investigation?: boolean;
    } = {},
  ) {
    const url = new URL(`aml/${pathSegment(auth.id)}/accounts`, this.baseUrl);

    /**
     * These are documents so it should bring all the rows
     * from start to the last and no pagination.
     */
    url.searchParams.set("offset", "0");
    url.searchParams.set("limit", "99999999");

    if (params.account !== undefined) {
      url.searchParams.set("h_payto", params.account);
    }

    addYesNoFilter(url, "investigation", params.investigation);
    addYesNoFilter(url, "open", params.open);
    addYesNoFilter(url, "high_risk", params.highRisk);

    const resp = await this.fetch(url, {
      headers: {
        Accept: mime,
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok: {
        return opFixedSuccess(resp, await resp.bytes());
      }
      case HttpStatusCode.NoContent:
        return opKnownHttpFailure(resp.status, resp, {
          code: TalerErrorCode.NONE,
        });
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotAcceptable:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-decisions
   *
   */
  async getAmlDecisions(
    auth: OfficerSession,
    params: PaginationParams & {
      account?: PaytoHash;
      active?: boolean;
      investigation?: boolean;
    } = {},
  ): Promise<
    | OperationFail<
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
      >
    | OperationOk<AmlDecisionsResponse>
  > {
    const url = new URL(`aml/${pathSegment(auth.id)}/decisions`, this.baseUrl);

    addPaginationParams(url, params);
    if (params.account !== undefined) {
      url.searchParams.set("h_payto", params.account);
    }
    if (params.active !== undefined) {
      url.searchParams.set("active", params.active ? "YES" : "NO");
    }
    if (params.investigation !== undefined) {
      url.searchParams.set(
        "investigation",
        params.investigation ? "YES" : "NO",
      );
    }

    const resp = await this.fetch(url, {
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForAmlDecisionsResponse());
      case HttpStatusCode.NoContent:
        return opFixedSuccess(resp, { records: [] });
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-legitimizations
   */
  async getAmlLegitimizations(
    officer: OfficerSession,
    params: PaginationParams & {
      account?: PaytoHash;
      active?: boolean;
    } = {},
  ): Promise<
    | OperationOk<LegitimizationMeasuresList>
    | OperationFail<HttpStatusCode.NotFound>
  > {
    const url = new URL(
      `aml/${pathSegment(officer.id)}/legitimizations`,
      this.baseUrl,
    );

    addPaginationParams(url, params);
    if (params.account !== undefined) {
      url.searchParams.set("h_payto", params.account);
    }
    if (params.active !== undefined) {
      url.searchParams.set("active", params.active ? "YES" : "NO");
    }

    const resp = await this.httpLib.fetch(url.href, {
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(officer.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForLegitimizationMeasuresList());
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      case HttpStatusCode.NoContent:
        return opFixedSuccess(resp, {
          measures: [],
        });
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-attributes-$H_NORMALIZED_PAYTO
   *
   */
  async getAmlAttributesForAccount(
    auth: OfficerSession,
    account: string,
    params: PaginationParams = {},
  ): Promise<
    | OperationOk<KycAttributes>
    | OperationFail<
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
      >
  > {
    const url = new URL(
      `aml/${pathSegment(auth.id)}/attributes/${pathSegment(account)}`,
      this.baseUrl,
    );

    addPaginationParams(url, params);
    const resp = await this.fetch(url, {
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForAmlKycAttributes());
      case HttpStatusCode.NoContent:
        return opFixedSuccess(resp, { details: [] });
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-attributes-$H_NORMALIZED_PAYTO
   *
   */
  async getAmlAttributesForAccountAsPdf(
    auth: OfficerSession,
    account: string,
    params: PaginationParams = {},
  ): Promise<
    | OperationOk<Uint8Array>
    | OperationFail<
        | HttpStatusCode.NoContent
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
        | HttpStatusCode.NotImplemented
      >
  > {
    const url = new URL(
      `aml/${pathSegment(auth.id)}/attributes/${pathSegment(account)}`,
      this.baseUrl,
    );

    addPaginationParams(url, params);
    const resp = await this.fetch(url, {
      headers: {
        Accept: "application/pdf",
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok: {
        return opFixedSuccess(resp, await resp.bytes());
      }
      case HttpStatusCode.NoContent:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotImplemented:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * Render one filled AML/KYC form without storing it.
   *
   * https://docs.taler.net/core/api-exchange.html#post--aml-$OFFICER_PUB-render-form
   */
  async renderAmlFormAsPdf(
    auth: OfficerSession,
    form: Record<string, unknown>,
  ): Promise<
    | OperationOk<Uint8Array>
    | OperationFail<
        | HttpStatusCode.BadRequest
        | HttpStatusCode.Forbidden
        | HttpStatusCode.PayloadTooLarge
        | HttpStatusCode.InternalServerError
        | HttpStatusCode.NotImplemented
      >
  > {
    const resp = await this.fetch(`aml/${pathSegment(auth.id)}/render-form`, {
      method: "POST",
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
      body: form,
      compress: this.preventCompression ? undefined : "deflate",
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opFixedSuccess(resp, await resp.bytes());
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.PayloadTooLarge:
      case HttpStatusCode.InternalServerError:
      case HttpStatusCode.NotImplemented:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--aml-$OFFICER_PUB-decision
   *
   */
  async makeAmlDesicion(
    auth: OfficerSession,
    decision: Omit<AmlDecisionRequest, "officer_sig">,
  ) {
    const body: AmlDecisionRequest = {
      officer_sig: encodeCrock(
        signAmlDecision(auth.__signingKey, decision),
      ) as any,
      ...decision,
    };
    const resp = await this.fetch(`aml/${auth.id}/decision`, {
      method: "POST",
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
      body,
      compress: this.preventCompression ? undefined : "deflate",
    });

    switch (resp.status) {
      case HttpStatusCode.NoContent: {
        await this.cacheEvictor.notifySuccess(
          TalerExchangeCacheEviction.MAKE_AML_DECISION,
        );
        return opEmptySuccess(resp);
      }
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-transfers-credit
   *
   */
  async getTransfersCredit(
    auth: OfficerSession,
    params: PaginationParams & {
      threshold?: AmountJson;
      account?: PaytoHash;
    } = {},
  ): Promise<
    | OperationOk<ExchangeTransferList>
    | OperationFail<
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
      >
  > {
    const url = new URL(
      `aml/${pathSegment(auth.id)}/transfers-credit`,
      this.baseUrl,
    );

    addPaginationParams(url, params);

    if (params.threshold) {
      url.searchParams.set("threshold", Amounts.stringify(params.threshold));
    }
    if (params.account) {
      url.searchParams.set("h_payto", params.account);
    }

    const resp = await this.fetch(url, {
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeTransferList());
      case HttpStatusCode.NoContent:
        return opFixedSuccess(resp, { transfers: [] });
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-transfers-debit
   *
   */
  async getTransfersDebit(
    auth: OfficerSession,
    params: PaginationParams & {
      threshold?: AmountJson;
      account?: PaytoHash;
    } = {},
  ): Promise<
    | OperationOk<ExchangeTransferList>
    | OperationFail<
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
      >
  > {
    const url = new URL(
      `aml/${pathSegment(auth.id)}/transfers-debit`,
      this.baseUrl,
    );

    addPaginationParams(url, params);

    if (params.threshold) {
      url.searchParams.set("threshold", Amounts.stringify(params.threshold));
    }
    if (params.account) {
      url.searchParams.set("h_payto", params.account);
    }

    const resp = await this.fetch(url, {
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeTransferList());
      case HttpStatusCode.NoContent:
        return opFixedSuccess(resp, { transfers: [] });
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-transfers-kycauth
   *
   */
  async getTransfersKycAuth(
    auth: OfficerSession,
    params: PaginationParams & {
      threshold?: AmountJson;
      account?: PaytoHash;
    } = {},
  ): Promise<
    | OperationOk<ExchangeTransferList>
    | OperationFail<
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
      >
  > {
    const url = new URL(
      `aml/${pathSegment(auth.id)}/transfers-kycauth`,
      this.baseUrl,
    );

    addPaginationParams(url, params);

    if (params.threshold) {
      url.searchParams.set("threshold", Amounts.stringify(params.threshold));
    }
    if (params.account) {
      url.searchParams.set("h_payto", params.account);
    }

    const resp = await this.fetch(url, {
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeTransferList());
      case HttpStatusCode.NoContent:
        return opFixedSuccess(resp, { transfers: [] });
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--aml-$OFFICER_PUB-wallet-credit
   *
   */
  async getWalletCredit(
    auth: OfficerSession,
    params: PaginationParams & {
      threshold?: AmountJson;
      account?: PaytoHash;
    } = {},
  ): Promise<
    | OperationOk<ExchangeTransferList>
    | OperationFail<
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
      >
  > {
    const url = new URL(
      `aml/${pathSegment(auth.id)}/wallet-credit`,
      this.baseUrl,
    );

    addPaginationParams(url, params);

    if (params.threshold) {
      url.searchParams.set("threshold", Amounts.stringify(params.threshold));
    }
    if (params.account) {
      url.searchParams.set("h_payto", params.account);
    }

    const resp = await this.fetch(url, {
      headers: {
        "Taler-AML-Officer-Signature": encodeCrock(
          signAmlQuery(auth.__signingKey),
        ),
      },
    });

    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeTransferList());
      case HttpStatusCode.NoContent:
        return opFixedSuccess(resp, { transfers: [] });
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * Request: POST /withdraw
   *
   * https://docs.taler.net/core/api-exchange.html#withdrawal
   */
  /**
   * Metadata about the exchange's terms of service: the current
   * `taler-terms-version` etag. (A GET is used since not every HTTP backend
   * supports HEAD.)
   *
   * https://docs.taler.net/core/api-exchange.html#terms-of-service
   */
  async getTermsMeta(): Promise<
    | OperationOk<{ etag: string | undefined }>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.NotImplemented>
  > {
    const resp = await this.fetch("terms");
    switch (resp.status) {
      case HttpStatusCode.Ok: {
        const etag = resp.headers.get("taler-terms-version") || undefined;
        return opFixedSuccess(resp, { etag });
      }
      // The body of these responses is not necessarily a Taler error JSON
      // (e.g. the exchange returns 501 "not configured" as plain text), so we
      // must not try to parse it.
      case HttpStatusCode.NotFound:
        return opKnownFailure(resp, HttpStatusCode.NotFound);
      case HttpStatusCode.NotImplemented:
        return opKnownFailure(resp, HttpStatusCode.NotImplemented);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--reserves-$RESERVE_PUB
   */
  async getReserveStatus(
    reservePub: string,
    longpoll: boolean = false,
  ): Promise<
    OperationOk<ReserveStatus> | OperationFail<HttpStatusCode.NotFound>
  > {
    const resp = await this.fetch(`reserves/${reservePub}`, {}, longpoll);
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForReserveStatus());
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--reserves-$RESERVE_PUB-close
   */
  async closeReserve(
    reservePub: string,
    body: ReserveCloseRequest,
  ): Promise<
    | OperationOk<ReserveCloseResponse>
    | OperationFail<HttpStatusCode.BadRequest>
    | OperationFail<HttpStatusCode.Forbidden>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.Conflict>
    | OperationAlternative<
        HttpStatusCode.UnavailableForLegalReasons,
        LegitimizationNeededResponse
      >
  > {
    const resp = await this.fetch(`reserves/${reservePub}/close`, {
      method: "POST",
      body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForReserveCloseResponse());
      case HttpStatusCode.UnavailableForLegalReasons:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForLegitimizationNeededResponse(),
        );
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--reserves-$RESERVE_PUB-history
   */
  async getReserveHistory(
    reservePub: string,
    signature: string,
    startOffset: number = 0,
  ): Promise<
    | OperationOk<ReserveHistory>
    | OperationAlternative<HttpStatusCode.NoContent, void>
    | OperationAlternative<HttpStatusCode.NotModified, void>
    | OperationFail<HttpStatusCode.Forbidden | HttpStatusCode.NotFound>
  > {
    const url = new URL(
      `reserves/${pathSegment(reservePub)}/history`,
      this.baseUrl,
    );
    if (startOffset !== 0) {
      url.searchParams.set("start", String(startOffset));
    }
    const resp = await this.fetch(url, {
      headers: {
        "Taler-Reserve-History-Signature": signature,
      },
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForReserveHistory());
      case HttpStatusCode.NoContent:
      case HttpStatusCode.NotModified:
        return opKnownFailureWithBody(resp, resp.status, undefined);
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--coins-$COIN_PUB-recoup
   */
  async recoupCoin(
    coinPub: string,
    body: RecoupRequest,
  ): Promise<
    | OperationOk<RecoupConfirmation>
    | OperationFail<
        | HttpStatusCode.BadRequest
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
        | HttpStatusCode.Gone
      >
  > {
    const resp = await this.fetch(`coins/${coinPub}/recoup`, {
      method: "POST",
      body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForRecoupConfirmation());
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
      case HttpStatusCode.Gone:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--coins-$COIN_PUB-recoup-refresh
   */
  async recoupRefreshCoin(
    coinPub: string,
    body: RecoupRefreshRequest,
  ): Promise<
    | OperationOk<RecoupConfirmation>
    | OperationFail<
        | HttpStatusCode.BadRequest
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
        | HttpStatusCode.Gone
      >
  > {
    const resp = await this.fetch(`coins/${coinPub}/recoup-refresh`, {
      method: "POST",
      body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForRecoupConfirmation());
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
      case HttpStatusCode.Gone:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--coins-$COIN_PUB-refund
   *
   * Returns the raw HTTP status as the failure case (never throws on a
   * documented non-2xx status), so the caller can decide how to react.
   */
  async refundCoin(
    coinPub: string,
    body: ExchangeRefundRequest,
  ): Promise<
    OperationOk<ExchangeRefundSuccessResponse> | OperationFail<HttpStatusCode>
  > {
    const resp = await this.fetch(`coins/${coinPub}/refund`, {
      method: "POST",
      body,
    });
    if (resp.status === HttpStatusCode.Ok) {
      return opSuccessFromHttp(resp, codecForExchangeRefundSuccessResponse());
    }
    return opKnownHttpFailure(resp.status, resp);
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--coins-$COIN_PUB-history
   */
  async getCoinHistory(
    coinPub: string,
    signature: string,
  ): Promise<
    OperationOk<CoinHistoryResponse> | OperationFail<HttpStatusCode.NotFound>
  > {
    const resp = await this.fetch(`coins/${coinPub}/history`, {
      headers: {
        "Taler-Coin-History-Signature": signature,
      },
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForCoinHistoryResponse());
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#post--batch-deposit
   */
  async batchDeposit(args: {
    body: ExchangeBatchDepositRequest;
  }): Promise<
    | OperationOk<BatchDepositSuccess>
    | OperationFail<
        | HttpStatusCode.BadRequest
        | HttpStatusCode.Forbidden
        | HttpStatusCode.NotFound
        | HttpStatusCode.Conflict
        | HttpStatusCode.Gone
        | HttpStatusCode.PreconditionFailed
        | HttpStatusCode.PayloadTooLarge
      >
    | OperationAlternative<
        HttpStatusCode.UnavailableForLegalReasons,
        LegitimizationNeededResponse
      >
  > {
    const resp = await this.fetch(`batch-deposit`, {
      method: "POST",
      body: args.body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
      case HttpStatusCode.Accepted:
        return opSuccessFromHttp(resp, codecForBatchDepositSuccess());
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
      case HttpStatusCode.Gone:
      case HttpStatusCode.PreconditionFailed:
      case HttpStatusCode.PayloadTooLarge:
        return opKnownHttpFailure(resp.status, resp);
      case HttpStatusCode.UnavailableForLegalReasons:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForLegitimizationNeededResponse(),
        );
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-exchange.html#get--deposits-$H_WIRE-$MERCHANT_PUB-$H_CONTRACT_TERMS-$COIN_PUB
   *
   * Long-polls until the deposit is wired (200) or the exchange reports a
   * pending (202) state.
   */
  async trackDeposit(args: {
    wireHash: string;
    merchantPub: string;
    contractTermsHash: string;
    coinPub: string;
    merchantSig: string;
    /**
     * Wait for the deposit status to change instead of returning the current
     * status immediately.  Defaults to true for backwards compatibility.
     */
    longpoll?: boolean;
  }): Promise<
    | OperationOk<TrackTransactionWired>
    | OperationAlternative<HttpStatusCode.Accepted, TrackTransactionAccepted>
  > {
    const url = new URL(
      `deposits/${args.wireHash}/${args.merchantPub}/${args.contractTermsHash}/${args.coinPub}`,
      this.baseUrl,
    );
    url.searchParams.set("merchant_sig", args.merchantSig);
    const longpoll = args.longpoll ?? true;
    if (longpoll) {
      url.searchParams.set("lpt", "1");
    }
    const resp = await this.fetch(url, {}, longpoll);
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForTackTransactionWired());
      case HttpStatusCode.Accepted:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForTackTransactionAccepted(),
        );
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  async withdraw(args: {
    body: ExchangeWithdrawRequest;
  }): Promise<
    | OperationOk<ExchangeWithdrawResponse>
    | OperationAlternative<
        HttpStatusCode.UnavailableForLegalReasons,
        LegitimizationNeededResponse
      >
    | OperationFail<HttpStatusCode.Forbidden>
    | OperationFail<HttpStatusCode.Gone>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.InternalServerError>
  > {
    const url = new URL(`withdraw`, this.baseUrl);
    const resp = await this.fetch(url, {
      method: "POST",
      body: args.body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeWithdrawResponse());
      case HttpStatusCode.UnavailableForLegalReasons:
        return opKnownAlternativeHttpFailure(
          resp,
          resp.status,
          codecForLegitimizationNeededResponse(),
        );
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.Gone:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.InternalServerError:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  async postBlindingPrepare(args: {
    body: BlindingPrepareRequest;
  }): Promise<
    | OperationOk<BlindingPrepareResponse>
    | OperationFail<HttpStatusCode.BadRequest>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.Gone>
    | OperationFail<HttpStatusCode.PayloadTooLarge>
    | OperationFail<HttpStatusCode.InternalServerError>
  > {
    const url = new URL(`blinding-prepare`, this.baseUrl);
    const resp = await this.fetch(url, {
      method: "POST",
      body: args.body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForBlindingPrepareResponse());
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Gone:
      case HttpStatusCode.PayloadTooLarge:
      case HttpStatusCode.InternalServerError:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * Request: POST /melt
   *
   * https://docs.taler.net/core/api-exchange.html#post--melt
   */
  async postMelt(args: {
    body: ExchangeMeltRequestV2;
  }): Promise<
    | OperationOk<ExchangeMeltResponse>
    | OperationFail<HttpStatusCode.Forbidden>
    | OperationFail<HttpStatusCode.NotFound>
    | OperationFail<HttpStatusCode.Gone>
    | OperationFail<HttpStatusCode.Conflict>
  > {
    const url = new URL(`melt`, this.baseUrl);
    const resp = await this.fetch(url, {
      method: "POST",
      body: args.body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeMeltResponse());
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Gone:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  async postRevealMelt(args: {
    body: ExchangeRefreshRevealRequestV2;
  }): Promise<
    | OperationOk<ExchangeRevealMeltResponseV2>
    | OperationFail<HttpStatusCode.Forbidden>
    | OperationFail<HttpStatusCode.Gone>
    | OperationFail<HttpStatusCode.Conflict>
  > {
    const url = new URL(`reveal-melt`, this.baseUrl);
    const resp = await this.fetch(url, {
      method: "POST",
      body: args.body,
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForExchangeRevealMeltResponseV2());
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.Gone:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }
}
